import { Button } from "@/components/ui/button";
import { HelpCircle, FileText, Loader2 } from "lucide-react";
import { WorkflowStep } from "@/types/workflow";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AIProgressBar } from "@/components/ai-assistant/AIProgressBar";

export interface AIHelpButtonProps {
  step: WorkflowStep;
  onAIHelp: () => void;
  isLoading: boolean;
  className?: string;
}

export function AIHelpButton({ 
  step, 
  onAIHelp, 
  isLoading, 
  className 
}: AIHelpButtonProps) {
  const [hasStoredHelp, setHasStoredHelp] = useState(false);
  const [progress, setProgress] = useState(0);

  // Check if there's stored AI help when component mounts
  useEffect(() => {
    const checkStoredHelp = async () => {
      if (!step.case_id) return;

      const { data } = await supabase
        .from('workflow_step_ai_help')
        .select('*')
        .eq('step_title', step.title)
        .eq('case_id', step.case_id)
        .single();
      
      setHasStoredHelp(!!data);
    };

    checkStoredHelp();
  }, [step.case_id, step.title]);

  // Handle click with progress simulation
  const handleClick = async () => {
    if (!isLoading) {
      setProgress(25);
      setTimeout(() => setProgress(50), 500);
      setTimeout(() => setProgress(75), 1000);
      onAIHelp();
      setTimeout(() => setProgress(100), 1500);
      setTimeout(() => setProgress(0), 2000);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        className={cn("flex items-center gap-2", className)}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Genererer AI-hjelp...
          </>
        ) : hasStoredHelp ? (
          <>
            <FileText className="h-4 w-4" />
            Vis AI Hjelp
          </>
        ) : (
          <>
            <HelpCircle className="h-4 w-4" />
            AI Hjelp
          </>
        )}
      </Button>
      {(isLoading || progress > 0) && (
        <AIProgressBar progress={progress} type="ai-help" />
      )}
    </div>
  );
}